import * as React from 'react';

import { rgba } from 'polished';
import styled from 'styled-components';

import { motion, AnimatePresence } from 'framer-motion';

import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import Button from '@components/actions/button';
import ArrowIcon from '@components/basics/icons/arrow';
import BoxTitleHeader from '@components/layout/box-title-header';

interface CollapsableBoxProps {
  className?: string;
  text?: React.ReactNode;
  title?: string;
  value?: string;
  isOpened?: boolean;
  customContent?: React.ReactNode;
  customHeader?: React.ReactNode;
  onClick?: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onChange?: (value: string) => void;
  whiteBg?: boolean;
  hasDarkButton?: boolean;
}

const CollapsableBox: React.FC<CollapsableBoxProps> = (props) => {
  const [isOpen, setIsOpen] = React.useState(
    props.isOpened ? props.isOpened : false
  );

  const [hideContent, setHideContent] = React.useState(false);

  const variants = {
    hidden: { opacity: 0, height: 0, marginTop: 0 },
    visible: { opacity: 1, height: '100%', marginTop: 30 },
  };

  return (
    <div className={props.className}>
      <Box className={`box-wrapper ${props.whiteBg ? 'white' : ''}`}>
        <Flex justifyContent="space-between" alignItems="center">
          {props.customHeader ? (
            props.customHeader
          ) : (
            <BoxTitleHeader
              className=""
              bigTitle
              value={props.value}
              title={props.title}
              text={!isOpen ? props.text : undefined}
              onChange={props.onChange}
            />
          )}
          <Button
            className={`${props.hasDarkButton ? 'dark-button' : ''}`}
            buttonType="add"
            onClick={() => setIsOpen(!isOpen)}
          >
            <ArrowIcon size={16} color="dark" up={!isOpen} />
          </Button>
        </Flex>
        <AnimatePresence onExitComplete={() => setHideContent(true)}>
          {props.customContent && !hideContent && (
            <CustomContent
              initial="hidden"
              animate={!isOpen ? 'visible' : 'hidden'}
              exit="hidden"
              variants={variants}
            >
              {props.customContent}
            </CustomContent>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {isOpen ? (
            <motion.div
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={variants}
            >
              {props.children}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </Box>
    </div>
  );
};

export default styled(CollapsableBox)`
  .box-wrapper {
    background: ${({ theme }) => rgba(theme.colors.secondaryDark, 0.04)};
    &.white {
      background: ${({ theme }) => theme.colors.background};
    }
  }
  .dark-button {
    background: ${({ theme }) => theme.colors.inputBackgroundAlternate};
    span {
      color: ${({ theme }) => theme.colors.primary} !important;
    }
  }
`;

const CustomContent = styled(motion.div)`
  margin-top: 30px;
`;
